import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { API_BASE } from '@/api/client';
import { Download, FileSpreadsheet, Users, Trash2, Search, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="card-moj p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl gradient-purple flex items-center justify-center">
        <Icon size={22} className="text-moj-gold"/>
      </div>
      <div>
        <div className="text-2xl font-bold text-moj-purple-deep">{value}</div>
        <div className="text-xs uppercase tracking-wider text-moj-gold-deep font-bold">{label}</div>
      </div>
    </div>
  );
}

function MediaConsentBadge({ reg }) {
  // Derive from granular fields if present, else fall back to legacy photo_consent.
  const granularKeys = [
    'consent_photos_activities',
    'consent_photos_website',
    'consent_photos_social',
    'consent_video_online',
    'consent_livestream',
  ];
  const status = reg.media_consent_status
    || (granularKeys.some((k) => reg[k] !== undefined)
      ? (granularKeys.every((k) => reg[k]) ? 'Full Media Consent'
         : granularKeys.some((k) => reg[k]) ? 'Partial Consent'
         : 'No Media Consent')
      : (reg.photo_consent === true ? 'Full Media Consent (legacy)'
         : reg.photo_consent === false ? 'No Media Consent'
         : 'Not Recorded'));
  const isNone = status.startsWith('No Media Consent');
  const isPartial = status.startsWith('Partial');
  const cls = isNone
    ? 'bg-red-100 text-red-800 border border-red-300'
    : isPartial
      ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
      : 'bg-green-100 text-green-800 border border-green-300';
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${cls}`}
      data-testid={`reg-media-status-${reg.id}`}
      title={status}
    >
      {status}
    </span>
  );
}

function RegistrationRow({ reg, onDelete }) {
  const [open, setOpen] = useState(false);
  const granularKeys = [
    ['consent_photos_activities', 'Photos (activities)'],
    ['consent_photos_website',    'Photos (website)'],
    ['consent_photos_social',     'Photos (social)'],
    ['consent_video_online',      'Video online'],
    ['consent_livestream',        'Live stream'],
  ];
  const hasGranular = granularKeys.some(([k]) => reg[k] !== undefined);
  return (
    <div className="card-moj overflow-hidden" data-testid={`reg-${reg.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left p-4 hover:bg-moj-cream-soft transition flex items-center gap-4">
        <div className="w-10 h-10 rounded-full gradient-purple flex items-center justify-center flex-shrink-0">
          <span className="font-serif text-moj-gold text-sm">{reg.student_name?.charAt(0) || 'S'}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-moj-purple-deep flex flex-wrap items-center gap-2">
            {reg.student_name}
            <MediaConsentBadge reg={reg} />
          </div>
          <div className="text-xs text-moj-charcoal/60 flex flex-wrap gap-x-3">
            <span>Age {reg.age} · {reg.gender}</span>
            <span>{reg.class_year}</span>
            <span>T-Shirt: {reg.tshirt_size}</span>
          </div>
        </div>
        <div className="hidden sm:block text-xs text-moj-charcoal/60 text-right">
          <div className="font-semibold text-moj-purple-deep">{reg.guardian_name}</div>
          <div>{reg.phone}</div>
        </div>
        <ChevronDown size={18} className={`text-moj-gold-deep transition ${open ? 'rotate-180' : ''}`}/>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-2 bg-moj-cream-soft border-t border-moj-gold/20 grid sm:grid-cols-2 gap-4 text-sm">
          <div><strong className="text-moj-purple-deep">Email:</strong> {reg.email}</div>
          <div><strong className="text-moj-purple-deep">Submitted:</strong> {reg.created_at?.slice(0, 19).replace('T', ' ')}</div>
          <div className="sm:col-span-2"><strong className="text-moj-purple-deep">Allergies / Medical:</strong> {reg.allergies}</div>
          <div><strong className="text-moj-purple-deep">Relationship:</strong> {reg.relationship}{reg.relationship_other ? ` (${reg.relationship_other})` : ''}</div>
          <div><strong className="text-moj-purple-deep">Alt. Contact:</strong> {reg.alternate_contact_name} · {reg.alternate_contact_phone}</div>

          {hasGranular ? (
            <div className="sm:col-span-2">
              <strong className="text-moj-purple-deep block mb-1">Media Consent (granular)</strong>
              <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1">
                {granularKeys.map(([k, label]) => (
                  <li key={k} className="flex items-center gap-2">
                    <span className={`inline-flex w-4 h-4 rounded border ${reg[k] ? 'bg-green-500 border-green-600' : 'bg-white border-moj-charcoal/30'}`} aria-hidden />
                    <span>{label}: <strong>{reg[k] ? 'Yes' : 'No'}</strong></span>
                  </li>
                ))}
              </ul>
              {reg.parent_full_name && (
                <div className="mt-3 pt-3 border-t border-moj-gold/20 text-xs text-moj-charcoal/80 space-y-0.5">
                  <div><strong>Signed by:</strong> {reg.parent_full_name} ({reg.parent_email || '—'}, {reg.parent_phone || '—'})</div>
                  <div><strong>Signature:</strong> {reg.consent_signature} · {reg.consent_date} · v{reg.consent_version}</div>
                </div>
              )}
            </div>
          ) : (
            <>
              <div><strong className="text-moj-purple-deep">Photo Consent (legacy):</strong> {reg.photo_consent === true ? 'Yes' : reg.photo_consent === false ? 'No' : 'Not recorded'}</div>
              <div><strong className="text-moj-purple-deep">Data Consent:</strong> {reg.consent_given ? 'Yes' : 'No'}</div>
            </>
          )}

          <div className="sm:col-span-2 flex justify-end">
            <button onClick={() => { if (window.confirm(`Delete registration for ${reg.student_name}?`)) onDelete(reg.id); }}
              className="text-xs px-3 py-1.5 rounded bg-red-600 text-white flex items-center gap-1">
              <Trash2 size={12}/> Delete registration
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function EventSelect({ events, selectedId, onChange }) {
  return (
    <select className="input-moj sm:max-w-md" value={selectedId || ''} onChange={(e) => onChange(e.target.value)} data-testid="reg-event-select">
      <option value="">Select an event…</option>
      {events.map((e) => (
        <option key={e.id} value={e.id}>
          {e.title} {e.form_type === 'vbs' ? '— VBS Form' : ''} {e.status !== 'Published' ? `(${e.status})` : ''}
        </option>
      ))}
    </select>
  );
}

export default function RegistrationsTab() {
  const qc = useQueryClient();
  const { data: events = [] } = useQuery({ queryKey: ['adm-events'], queryFn: async () => (await api.get('/admin/events')).data });
  // Default to first VBS-form event
  const vbsEvents = useMemo(() => events.filter((e) => e.form_type === 'vbs'), [events]);
  const [selectedId, setSelectedId] = useState('');
  const [search, setSearch] = useState('');

  // Auto-select first VBS event when events load
  useEffect(() => {
    if (!selectedId && vbsEvents.length > 0) setSelectedId(vbsEvents[0].id);
  }, [vbsEvents, selectedId]);

  const { data: regs = [], isLoading } = useQuery({
    queryKey: ['adm-registrations', selectedId],
    queryFn: async () => (await api.get(`/admin/events/${selectedId}/registrations`)).data,
    enabled: !!selectedId,
  });

  const del = useMutation({
    mutationFn: async (regId) => (await api.delete(`/admin/events/${selectedId}/registrations/${regId}`)).data,
    onSuccess: () => { toast.success('Registration deleted'); qc.invalidateQueries({ queryKey: ['adm-registrations', selectedId] }); },
  });

  const filtered = useMemo(() => {
    if (!search.trim()) return regs;
    const q = search.toLowerCase();
    return regs.filter((r) =>
      (r.student_name || '').toLowerCase().includes(q) ||
      (r.guardian_name || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.phone || '').toLowerCase().includes(q) ||
      (r.allergies || '').toLowerCase().includes(q)
    );
  }, [regs, search]);

  const selectedEvent = events.find((e) => e.id === selectedId);

  const downloadXlsx = async () => {
    if (!selectedId) return;
    try {
      const token = localStorage.getItem('moj_admin_token');
      const resp = await fetch(`${API_BASE}/admin/events/${selectedId}/registrations/export`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dispositionHeader = resp.headers.get('Content-Disposition') || '';
      const m = dispositionHeader.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `registrations-${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filtered.length || regs.length} registrations`);
    } catch (e) {
      toast.error('Could not download. Please try again.');
    }
  };

  // Quick stats
  const stats = useMemo(() => {
    const total = regs.length;
    const photoYes = regs.filter((r) => r.photo_consent).length;
    const allergyCount = regs.filter((r) => r.allergies && r.allergies.toLowerCase() !== 'none').length;
    return { total, photoYes, allergyCount };
  }, [regs]);

  return (
    <div className="space-y-6">
      <div className="card-moj p-5">
        <div className="flex items-center gap-3 mb-3">
          <FileSpreadsheet size={22} className="text-moj-gold-deep"/>
          <h2 className="font-serif text-xl text-moj-purple-deep">Event Registrations</h2>
        </div>
        <p className="text-sm text-moj-charcoal/70 mb-4">View and export detailed registrations submitted via VBS-style forms (e.g. Summer Bible Camp). For light-touch event RSVPs use the Events tab.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <label className="label-moj">Event</label>
            <EventSelect events={events} selectedId={selectedId} onChange={setSelectedId}/>
          </div>
          <button onClick={downloadXlsx} disabled={!selectedId || regs.length === 0}
            className="btn-gold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="export-xlsx-btn">
            <Download size={16}/> Export XLSX
          </button>
        </div>
      </div>

      {selectedEvent && (
        <>
          <div className="grid sm:grid-cols-3 gap-4">
            <StatCard label="Total registrations" value={stats.total} icon={Users}/>
            <StatCard label="Photo consent (Yes)" value={stats.photoYes} icon={Users}/>
            <StatCard label="Medical notes / allergies" value={stats.allergyCount} icon={Users}/>
          </div>

          <div className="card-moj p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-moj-gold-deep"/>
              <input className="input-moj pl-9" placeholder="Search by name, email, phone, allergy…"
                value={search} onChange={(e) => setSearch(e.target.value)} data-testid="reg-search"/>
            </div>
            <div className="text-sm text-moj-charcoal/70">
              {filtered.length} of {regs.length} {regs.length === 1 ? 'registration' : 'registrations'}
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-12 text-moj-charcoal/60">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="card-moj p-10 text-center text-moj-charcoal/60">
              {regs.length === 0
                ? 'No registrations submitted for this event yet.'
                : 'No registrations match your search.'}
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((reg) => (
                <RegistrationRow key={reg.id} reg={reg} onDelete={del.mutate}/>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
